import { FC, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import styled from 'styled-components';
import type { Wrestler } from '../types';

// CSS Structure
import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import SingleCardWrestler from '../components/wrestlers_components/SingleCardWrestler';

import SearchPpv from '../components/ppv_components/SearchPpv';
import BoxPage from '../components/ppv_components/BoxPage';
import FilterListWrestler from '../components/wrestlers_components/FilterListWrestler';
import { LoadingState } from '../components/stats_components/LoadingState';
import colors from '../styles/colors';

const WRESTLERS_PER_PAGE = 24;

interface LoaderData {
    data: {
        data: Wrestler[];
    };
}

const MainContainer = styled.div`
    border-radius: 5px;
`;

const StickyToolbar = styled.div`
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: ${colors.bgColorBlack};
    backdrop-filter: blur(6px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 8px 0;
    width: 100%;
`;

const TopSize = styled.div`
    display: flex;
    justify-content: space-evenly;
    width: 100%;
    padding: 5px;
`;

const ListWrestlersContainer = styled.div`
    width: 100%;
    min-height: calc(100vh - 70px);
    margin-top: 20px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 200px);
    color: rgba(255, 255, 255, 0.4);
    gap: 12px;

    & .empty-title {
        font-size: 18px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.6);
    }

    & .empty-sub {
        font-size: 14px;
    }
`;

const ListWrestlers: FC = () => {
    const loaderData = useLoaderData() as LoaderData;
    const allWrestlers = loaderData?.data?.data ?? [];

    // Ordinamento
    const [orderBy, setOrderBy] = useState('name');
    const [isOrderDesc, setIsOrderDesc] = useState(false);
    const isOrderByName = orderBy === 'name';
    const isOrderByHeight = orderBy === 'height';
    const isOrderByWeight = orderBy === 'weight';

    // Filtri
    const [isFilterMan, setIsFilterMan] = useState(false);
    const [isFilterWomen, setIsFilterWomen] = useState(false);
    const [isFilterManager, setIsFilterManager] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Paginazione
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(() => Math.max(1, Math.ceil(allWrestlers.length / WRESTLERS_PER_PAGE)));
    const [currentPageWrestlers, setCurrentPageWrestlers] = useState<Wrestler[]>(() => allWrestlers.slice(0, WRESTLERS_PER_PAGE));

    // Loading per navigazione alla pagina wrestler
    const [isLoadingSwitch, setIsLoadingSwitch] = useState(false);

    function filterAndSort(
        search: string,
        filterMan: boolean,
        filterWomen: boolean,
        filterManager: boolean,
        order: string,
        orderDesc: boolean
    ): Wrestler[] {
        let result = [...allWrestlers];

        if (search) {
            result = result.filter(w =>
                w.name.toLowerCase().includes(search.toLowerCase())
            );
        }
        if (filterMan) result = result.filter(w => !w.is_female);
        if (filterWomen) result = result.filter(w => !!w.is_female);
        if (filterManager) result = result.filter(w => !!w.is_manager);

        result.sort((a, b) => {
            // attivi sempre prima
            const activeA = a.is_active ? 0 : 1;
            const activeB = b.is_active ? 0 : 1;
            if (activeA !== activeB) return activeA - activeB;

            let aVal: number | string;
            let bVal: number | string;

            if (order === 'height') {
                aVal = a.height ?? 0;
                bVal = b.height ?? 0;
            } else if (order === 'weight') {
                aVal = a.weight ?? 0;
                bVal = b.weight ?? 0;
            } else {
                aVal = a.name.toLowerCase();
                bVal = b.name.toLowerCase();
            }

            if (aVal < bVal) return orderDesc ? 1 : -1;
            if (aVal > bVal) return orderDesc ? -1 : 1;
            return 0;
        });

        return result;
    }

    function applyPage(filtered: Wrestler[], page: number): void {
        const pages = Math.max(1, Math.ceil(filtered.length / WRESTLERS_PER_PAGE));
        const validPage = Math.max(1, Math.min(page, pages));
        setTotalPages(pages);
        setCurrentPage(validPage);
        setCurrentPageWrestlers(
            filtered.slice((validPage - 1) * WRESTLERS_PER_PAGE, validPage * WRESTLERS_PER_PAGE)
        );
    }

    function handleSearchChange(value: string): void {
        setSearchText(value);
        const filtered = filterAndSort(value, isFilterMan, isFilterWomen, isFilterManager, orderBy, isOrderDesc);
        applyPage(filtered, 1);
    }

    function updateListWrestlerWithOrder(
        value: string,
        newOrderDesc: boolean = isOrderDesc,
        newFilterMan: boolean = isFilterMan,
        newFilterWomen: boolean = isFilterWomen,
        newFilterManager: boolean = isFilterManager
    ): void {
        setOrderBy(value);
        setIsOrderDesc(newOrderDesc);
        setIsFilterMan(newFilterMan);
        setIsFilterWomen(newFilterWomen);
        setIsFilterManager(newFilterManager);
        const filtered = filterAndSort(searchText, newFilterMan, newFilterWomen, newFilterManager, value, newOrderDesc);
        applyPage(filtered, 1);
    }

    function handlePageChange(page: number): void {
        const filtered = filterAndSort(searchText, isFilterMan, isFilterWomen, isFilterManager, orderBy, isOrderDesc);
        applyPage(filtered, page);
        window.scrollTo(0, 0);
    }

    const switchIsLoading = (value: boolean): void => {
        setIsLoadingSwitch(value);
    };

    if (isLoadingSwitch) {
        return <LoadingState />;
    }

    return (
        <CommonWrapper>
            <MainContainer className="container mt-2 mb-4">

                <StickyToolbar>
                    <TopSize className="row">
                        <SearchPpv
                            listPpv={currentPageWrestlers}
                            onFilterTextChange={handleSearchChange}
                        />

                        <BoxPage
                            className="col-4"
                            maxPageApi={totalPages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />

                        <FilterListWrestler
                            onOrderChange={updateListWrestlerWithOrder}
                            isOrderByName={isOrderByName}
                            isOrderByHeight={isOrderByHeight}
                            isOrderByWeight={isOrderByWeight}
                            isOrderDesc={isOrderDesc}
                            isFilterMan={isFilterMan}
                            isFilterWomen={isFilterWomen}
                            isFilterManager={isFilterManager}
                            currentOrder={orderBy}
                        />
                    </TopSize>
                </StickyToolbar>

                {currentPageWrestlers.length === 0 && (
                    <EmptyState>
                        <span className="empty-title">Nessun wrestler trovato</span>
                        <span className="empty-sub">Prova a cambiare i filtri o la ricerca</span>
                    </EmptyState>
                )}

                {currentPageWrestlers.length > 0 && (
                    <ListWrestlersContainer className='container'>
                        <div className="row">
                            {currentPageWrestlers.map((wrestler, index) => (
                                <SingleCardWrestler
                                    key={'wrestler-' + index + '-' + wrestler.id}
                                    wrestler={wrestler}
                                    switchIsLoading={switchIsLoading}
                                />
                            ))}
                        </div>
                    </ListWrestlersContainer>
                )}

                <BoxPage
                    className="d-flex justify-content-center mt-2"
                    maxPageApi={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />

            </MainContainer>
        </CommonWrapper>
    );
};

export default ListWrestlers;
